/**
 * The real-data provider: one fetch of `/api/data/snapshot`, rehydrated once per session.
 *
 * The snapshot is columnar (30k SKUs as parallel arrays — 3MB raw instead of 21MB of objects),
 * so this file is the only place that knows the wire shape. Everything downstream sees the
 * same `Sku[]` / `Site` / `ReportRow[]` the demo provider serves.
 *
 * Deliberate deviations from the repo's usual API rules, per the data-layer decisions:
 *   - the payload is raw JSON, NOT routed through the generated openapi-ts SDK; the
 *     hand-written types in `types.ts` are the single source of truth (decision 2);
 *   - the reference tables below (`CHANNELS`, `GEO_MARKETS`, `FIELD_SCALE`, `REPORT_META`) and
 *     `RELO_STOPS` in `math.ts` are MUTABLE module singletons.
 *     The plot and the lenses need them synchronously at build /
 *     render time while the snapshot arrives asynchronously, so `hydrate()` overwrites them
 *     IN PLACE before `getSkus()` resolves — i.e. before anything that reads them can paint.
 *     Their initial contents are the demo tables, which is exactly what demo mode needs, so
 *     nothing has to branch on the data source.
 *
 * On 404 / network failure every method falls back to the demo provider (G-005): a broken
 * backend must not blank the screen mid-demo.
 */

import { logger } from "@/lib/logger";

import { type AgentAnchors, capacityAnswer, fieldAnswer } from "./agent";
import {
  CHANNELS as DEMO_CHANNELS,
  DEMO_LABELS,
  demoDataProvider,
  GEO_MARKETS as DEMO_GEO_MARKETS,
  REPORT_META as DEMO_REPORT_META,
} from "./demo";
import { leverPool, RELO_STOPS } from "./math";
import type { DataProvider } from "./provider";
import {
  annualisedNote,
  BASIS,
  CUR,
  DEPTH,
  describeBasis,
  MONTH_SLOTS,
  monthSlots,
  SIM_FEEDS,
} from "./served";
import type {
  AgentContext,
  Band,
  CapacityModel,
  Channel,
  DataLabels,
  GeoMarket,
  Lever,
  LeverFamily,
  LeverId,
  PickLocation,
  ReportId,
  ReportMeta,
  ReportRow,
  Site,
  Sku,
  SpaceLeverId,
} from "./types";

const SNAPSHOT_URL = "/api/data/snapshot";

/* ============ wire shape (the build script's contract) ============ */

interface ProvenanceEntry {
  feed: string;
  /**
   * `input` is a figure no export of sales or stock can contain — a warehouse's pick-face ceiling,
   * next year's launch plan — stated and sourced instead of derived. It is deliberately neither
   * `real` nor `simulated`: calling it simulated read as "made up", and deriving it would mean
   * inventing it.
   */
  status: "real" | "simulated" | "derived" | "input";
  note: string;
}

interface Calibration {
  velMax: number;
  dxVelMax: number;
  bandCuts: number[];
  invMax: number;
  valueMax: number;
  /**
   * How `Sku.faces` was derived, including the build's own one-sentence statement of it.
   *
   * Optional: an older snapshot omits it and the screens fall back to their seeded wording.
   * `rule` is rendered verbatim rather than reconstructed, so the divisor and the sentence cannot
   * disagree.
   */
  pickFace?: {
    boxesPerFace: number;
    portfolioFaces: number;
    sitePickCap: number;
    vsSitePickCap: number;
    rule: string;
    /** The slotting rule `Sku.pickReco` was derived on, in the build's own words. */
    recoRule?: string;
    /** SKUs recommended for each pick location, keyed by its label. */
    recoCounts?: Record<string, number>;
  };
}

interface SnapshotPayload {
  generatedAt: string;
  source: {
    salesLinesRead: number;
    heldSkus: number;
    monthsCovered: string[];
    /**
     * Date of the stock position, `YYYY-MM-DD` (a list when the drop merges exports — the
     * newest wins). Optional: a drop whose inventory feed carries no readable snapshot date
     * says so on screen instead of borrowing the sales window's dates.
     */
    inventoryAsOf?: string | string[];
    /** First and last sales document date counted, `YYYY-MM-DD`. */
    salesWindow?: { first?: string; last?: string; from?: string; to?: string; days?: number };
    inventorySnapDates?: string[];
    /** Days from the end of the sales window to the stock position, as the build measures it. */
    inventoryGapDays?: number;
    /**
     * The non-mover population split by which lever can act on it, as the build partitions it.
     *
     * `npiProtected + relo + conv === total`. Needed because a lever's *family pool* is not the
     * same set: it includes movers, so quoting `leverPool` beside the non-mover candidate count
     * overshot the partition. Optional so an older snapshot still renders.
     */
    nonMovers?: { total: number; npiProtected: number; relo: number; conv: number };
  };
  provenance: ProvenanceEntry[];
  calibration: Calibration;
  currency: { reporting: string };
  channels: Channel[];
  geoMarkets: GeoMarket[];
  skus: {
    n: number;
    brands: string[];
    packs: string[];
    pickLocations: PickLocation[];
    launchLabels: string[];
    /** Exactly one of `monShared` / `mon` is non-null. */
    monShared: number[] | null;
    mon: number[][] | null;
    code: string[];
    name: string[];
    brandIdx: number[];
    packIdx: number[];
    dx: number[];
    price: number[];
    vel: number[];
    inv: number[];
    value: number[];
    band: number[];
    flags: number[];
    faces: number[];
    pickReco: number[];
    lastMovedQ: number[];
    accounts: number[];
    launchIdx: number[];
    chan: number[][];
    /** Per SKU, a list of `[geoMarket index, percent]` pairs. */
    geo: [number, number][][];
  };
  site: Site;
  capacity: CapacityModel;
  levers: Lever[];
  leverFamilies: Record<SpaceLeverId, LeverFamily[]>;
  reports: Record<ReportId, ReportRow[]>;
}

/* ============ live reference tables (see the file header) ============ */

/** Route-to-market channels — real trade channels in api mode, any length. */
export const CHANNELS: Channel[] = [...DEMO_CHANNELS];
/** Account geography — real regions in api mode, any length. */
export const GEO_MARKETS: GeoMarket[] = [...DEMO_GEO_MARKETS];
/** Axis maxima and band cuts the plot draws with; overwritten from `calibration`. */
export const FIELD_SCALE: Calibration = {
  velMax: 10000,
  dxVelMax: 420,
  bandCuts: [200, 850, 3200],
  invMax: 3000,
  valueMax: 2000000,
};
/** Report titles, columns and narrative copy; the narratives are re-derived in api mode. */
export const REPORT_META: Record<ReportId, ReportMeta> = {
  storage: { ...DEMO_REPORT_META.storage },
  nonmover: { ...DEMO_REPORT_META.nonmover },
};

const BANDS: Band[] = ["A", "B", "C", "D"];
/** `flags` bitmask, per the snapshot contract. */
const F_WORTH = 1;
const F_PREMIUM = 2;
const F_NEW = 4;

/* ============ rehydration ============ */

interface Hydrated {
  skus: Sku[];
  payload: SnapshotPayload;
  anchors: AgentAnchors;
  labels: DataLabels;
}

/** One indexed pass over the columns; the only allocation per SKU is the Sku and its arrays. */
function hydrate(p: SnapshotPayload): Hydrated {
  // In place, so anything holding a reference (the plot, the lenses) sees the real tables.
  CHANNELS.splice(0, CHANNELS.length, ...p.channels);
  GEO_MARKETS.splice(0, GEO_MARKETS.length, ...p.geoMarkets);
  FIELD_SCALE.velMax = p.calibration.velMax;
  FIELD_SCALE.dxVelMax = p.calibration.dxVelMax;
  FIELD_SCALE.invMax = p.calibration.invMax;
  FIELD_SCALE.valueMax = p.calibration.valueMax;
  FIELD_SCALE.bandCuts.splice(0, FIELD_SCALE.bandCuts.length, ...p.calibration.bandCuts);

  // The relocation spectrum's copy is window-relative in the served payload ("0 shipments in
  // the window"), so the sliders must render the served stops, not the demo table's "in 2025".
  const served = p.levers.find((l) => l.id === "relo")?.reloStops;
  if (served?.length) {
    RELO_STOPS.splice(0, RELO_STOPS.length, ...served);
  }

  // What the drop covers and dates, for every caption that would otherwise assume it.
  Object.assign(
    BASIS,
    describeBasis({
      monthsCovered: p.source.monthsCovered,
      salesWindow: p.source.salesWindow,
      inventoryAsOf: p.source.inventoryAsOf,
      inventorySnapDates: p.source.inventorySnapDates,
      gapDays: p.source.inventoryGapDays,
    }),
  );
  if (!BASIS.inventoryAsOf) {
    logger.warn("[data] drop carries no inventory snapshot date — held figures cannot be dated");
  }
  const slots = monthSlots(p.source.monthsCovered);
  if (slots.length) {
    MONTH_SLOTS.splice(0, MONTH_SLOTS.length, ...slots);
  } else {
    logger.warn("[data] no fiscal month in monthsCovered parsed:", p.source.monthsCovered);
  }
  // `simulated` only — a `derived` feed is computed from real data and is not a fabrication.
  SIM_FEEDS.splice(
    0,
    SIM_FEEDS.length,
    ...p.provenance.filter((e) => e.status === "simulated").map((e) => e.feed),
  );

  const k = p.skus;
  const n = k.n;
  // Single-month drops carry one shared monthly mix — share the ARRAY, don't copy it 30k times.
  const shared = k.monShared;
  const skus: Sku[] = new Array<Sku>(n);
  for (let i = 0; i < n; i++) {
    const flags = k.flags[i] ?? 0;
    // `chan` arrives as integer percents; `Sku.chan` is documented as a normalised mix, so
    // scale it by its own total in place (no extra allocation, works for percents or fractions).
    const chan = k.chan[i] ?? [];
    let chanTot = 0;
    for (let j = 0; j < chan.length; j++) {
      chanTot += chan[j] ?? 0;
    }
    if (chanTot > 0 && chanTot !== 1) {
      for (let j = 0; j < chan.length; j++) {
        chan[j] = (chan[j] ?? 0) / chanTot;
      }
    }
    const pairs = k.geo[i] ?? [];
    const geo: GeoMarket[] = new Array<GeoMarket>(pairs.length);
    for (let j = 0; j < pairs.length; j++) {
      const market = GEO_MARKETS[pairs[j]?.[0] ?? 0];
      geo[j] = market ?? (GEO_MARKETS[0] as GeoMarket);
    }
    skus[i] = {
      id: i,
      name: k.name[i] ?? k.code[i] ?? String(i),
      brand: k.brands[k.brandIdx[i] ?? 0] ?? "",
      size: k.packs[k.packIdx[i] ?? 0] ?? "",
      type: k.dx[i] ? "Dx" : "Rx",
      price: k.price[i] ?? 0,
      vel: k.vel[i] ?? 0,
      inv: k.inv[i] ?? 0,
      value: k.value[i] ?? 0,
      band: BANDS[k.band[i] ?? 3] ?? "D",
      worth: (flags & F_WORTH) !== 0,
      premiumSlow: (flags & F_PREMIUM) !== 0,
      isNew: (flags & F_NEW) !== 0,
      faces: k.faces[i] ?? 1,
      pickReco: k.pickLocations[k.pickReco[i] ?? 0] ?? "Small-bin",
      lastMovedQ: k.lastMovedQ[i] ?? 0,
      accounts: k.accounts[i] ?? 0,
      launched: k.launchLabels[k.launchIdx[i] ?? 0] ?? "",
      chan,
      mon: shared ?? k.mon?.[i] ?? [],
      geo,
    };
  }

  // The reporting currency lives beside the breakdown, so the widgets can label the total —
  // and in `CUR` for the plot, which paints money with no `Site` in scope.
  p.site.ntsCurrency ??= p.currency.reporting;
  CUR.nts = p.site.ntsCurrency || CUR.nts;

  // The build calibrates the face-depth divisor from this drop's own inventory distribution and
  // serves the sentence describing it. Render that rather than restating it locally: the previous
  // local wording could not name the divisor, so it drifted from the number beside it.
  if (p.calibration.pickFace?.rule) {
    DEPTH.rule = p.calibration.pickFace.rule;
  }

  return {
    skus,
    payload: p,
    anchors: {
      skus,
      site: p.site,
      capacity: p.capacity,
      levers: p.levers,
      families: p.leverFamilies,
      channels: CHANNELS,
      reloStops: RELO_STOPS,
    },
    labels: labelsFrom(p),
  };
}

/* ============ truthful provenance copy (R-005) ============ */

const pretty = (feed: string) => feed.replace(/-/g, " ");
const list = (feeds: string[]) => feeds.map(pretty).join(", ");

/**
 * Badge + caption for the rail and the reports header, driven by the snapshot's own
 * provenance array — never a hardcoded list of which feeds are real, because that changes
 * with every drop.
 *
 * The span belongs to the SALES feed only. Stock on hand is a separate position on its own
 * date, so it is named separately: one span across both would assert that the boxes on the
 * shelf and the boxes shipped describe the same weeks, which on this drop they do not.
 */
function labelsFrom(p: SnapshotPayload): DataLabels {
  const real = p.provenance.filter((e) => e.status === "real").map((e) => e.feed);
  const sim = p.provenance.filter((e) => e.status === "simulated").map((e) => e.feed);
  // Every money figure on the screen is the observed window scaled to a year, so the rail says
  // so once and the whole screen inherits it. The factor is the drop's own month count, not 12.
  const months = p.source.monthsCovered.length;
  const parts = [
    `Real ${list(real)} — sales for ${BASIS.salesSpan}, ${p.source.salesLinesRead.toLocaleString()} lines, ${p.source.heldSkus.toLocaleString()} SKUs held, in ${p.currency.reporting}.`,
    months >= 12
      ? "Annual money figures are the twelve fiscal months observed."
      : annualisedNote(months),
    BASIS.inventoryAsOf
      ? `Stock on hand is a separate position as at ${BASIS.inventoryAsOf}${BASIS.gap ? `, ${BASIS.gap} that sales window` : ""}.`
      : "The inventory feed carries no readable snapshot date, so its stock position cannot be dated against that sales window.",
  ];
  if (sim.length) {
    parts.push(`Simulated (no feed in this drop): ${list(sim)}.`);
  }
  // Stated planning figures are called out separately from simulations: no export of sales and
  // stock can carry a building's pick-face ceiling or next year's launch plan, so they are inputs
  // with a source, not fabrications — and the badge must not count them as either measured or fake.
  const inputs = p.provenance.filter((e) => e.status === "input").map((e) => e.feed);
  if (inputs.length) {
    parts.push(`Planning inputs, stated not measured: ${list(inputs)}.`);
  }
  return {
    badge: sim.length ? `Real data · ${sim.length} simulated` : "Real data",
    caption: parts.join(" "),
    notes: p.provenance.map((e) => `${pretty(e.feed)} — ${e.note}`),
  };
}

/**
 * Both report narratives are re-derived from the served data (R-005):
 *   - the non-mover copy has to state the NPI share, or 9.5k reads as 9.5k dead SKUs;
 *   - the slotting copy must state that it is a RECOMMENDATION: no drop has carried a
 *     pick-location feed, so where a SKU sits today is unknown and no mismatch can be counted.
 */
function reportNarratives(h: Hydrated) {
  const months = h.payload.source.monthsCovered.length;
  const observed = months === 1 ? "single fiscal month" : `${months} fiscal months`;
  let dead = 0;
  let deadNpi = 0;
  let deadNoFace = 0;
  for (const s of h.skus) {
    if (s.lastMovedQ >= 1) {
      dead++;
      if (s.isNew) {
        deadNpi++;
      }
      if (s.faces === 0) {
        deadNoFace++;
      }
    }
  }
  const nm = h.payload.source.nonMovers;
  const reloNm = nm ? nm.relo : leverPool(h.payload.leverFamilies.relo ?? [], 0);
  const convNm = nm ? nm.conv : leverPool(h.payload.leverFamilies.conv ?? [], 0);
  /** True only when the two lever counts genuinely add up to the candidate count above. */
  const partitions = reloNm + convNm === dead - deadNpi;
  const leverSplit =
    ` The two capacity levers scope them separately — <b>SKU Relocation</b> counts ${reloNm.toLocaleString()} at its non-mover threshold and <b>Rx→Dx Conversion</b> ${convNm.toLocaleString()}, because the slow Dx fitting sets belong to the conversion pool rather than the relocation one.` +
    (partitions
      ? " Together they account for every candidate above."
      : " Each lever also reaches SKUs that do ship, so the two counts are not a partition of the candidates above.") +
    " The accepted set here is the review; the lever counts are what the capacity scenarios move.";
  REPORT_META.nonmover.narrative = (rows) =>
    `<b>${rows.toLocaleString()} SKUs</b> did not ship in the <b>${observed}</b> this drop covers (${BASIS.salesSpan})` +
    // "Did not ship" is read over the sales window; "holds a pick face" is read off the stock
    // position, a different date — and it is not true of every row: a non-mover can be held with
    // 0 boxes on hand (faces floors to 0, not 1, in that case), so the count is stated instead of
    // asserted of "each".
    (deadNoFace
      ? `; <b>${(rows - deadNoFace).toLocaleString()}</b> of them still hold a pick face, and <b>${deadNoFace.toLocaleString()}</b> show 0 boxes on hand.`
      : ", yet each still holds a pick face.") +
    (BASIS.inventoryAsOf
      ? ` The stock position behind those pick faces is as at <b>${BASIS.inventoryAsOf}</b>${BASIS.gap ? `, ${BASIS.gap} that window` : ""} — a SKU that shipped after the window would still appear here.`
      : " No dated stock position accompanies this data, so the pick faces cannot be dated against that window.") +
    (deadNpi
      ? ` <b>${deadNpi.toLocaleString()}</b> of them (${Math.round((deadNpi / Math.max(dead, 1)) * 100)}%) carry the NPI flag — newly launched and not yet shipping rather than dead stock, and protected from every lever. The other <b>${(dead - deadNpi).toLocaleString()}</b> are genuine relocation or de-list candidates.`
      : "") +
    " Relocating or de-listing them frees space without touching anything that moves." +
    // The candidate count above is NOT either lever's family pool: a pool includes movers, so
    // quoting `leverPool` here made the two lever figures sum to 29 MORE than the candidate count
    // the same paragraph had just partitioned. The build serves the non-mover population split by
    // lever (npiProtected + relo + conv === total), which is the only pair that reconciles. Fall
    // back to the pools when an older snapshot omits it, and in that case do not claim a partition.
    leverSplit;

  // The slotting review is a RECOMMENDATION, and the copy says so in its own first clause — there
  // is no current-slot feed in any drop yet, so a mismatch cannot be counted and is not claimed.
  // The rule is the build's own sentence, so the report cannot describe a different rule from the
  // one it applied.
  const lanes = h.skus.filter((s) => s.pickReco === "A-frame").length;
  const auto = h.skus.filter((s) => s.pickReco === "OSR automation").length;
  const rule = h.payload.calibration.pickFace?.recoRule;
  REPORT_META.storage.narrative = (rows) =>
    `<b>${rows.toLocaleString()} SKUs</b> move fast enough — and are held deep enough — to belong in a dedicated A-frame lane (<b>${lanes.toLocaleString()}</b>) or in automation (<b>${auto.toLocaleString()}</b>), fastest first. ` +
    `<b>This is a recommendation, not a mismatch list:</b> this data carries no pick-location feed, so nothing below claims where a SKU sits today${
      rule ? ` — it is ${rule}` : ""
    }. ` +
    "Check each against the WMS: Accept or Remove, and the accepted set is what you action. " +
    `Depth is ${DEPTH.rule}.`;
}

/* ============ single-flight load ============ */

/** Resolves to the parsed snapshot, or `null` when we fell back to the demo provider. */
let inflight: Promise<Hydrated | null> | null = null;
/**
 * Why the fallback fired, when it was NOT the expected "this deployment serves no snapshot" (404).
 *
 * A transient 500, a 401 or a dropped connection lands in demo data for the rest of the session
 * exactly like a 404 does, and the badge then read plain `Demo data` — true, but it describes a
 * deliberate demo deployment, not a real one whose feed just broke. The operator has no way to tell
 * those apart, and the difference decides whether they reload or carry on presenting. So the reason
 * is kept and the labels say it.
 */
let degraded: string | null = null;

/** Statuses worth trying again: the request could succeed unchanged a moment later. A 401/403 is an
 *  expired session and a 400 is a bad request — neither improves on a retry. */
const transient = (status: number) => status >= 500 || status === 408 || status === 429;
const ATTEMPTS = 3;
const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Fetch the payload, retrying a transient failure BEFORE anything has been served.
 *
 * This is where a flaky feed is meant to recover, and the only place it can recover safely: the
 * single-flight promise has not resolved yet, so no screen holds demo data and there is nothing for
 * a late success to contradict. Retrying *after* the fallback resolves is the unsafe one — see the
 * note in `load()`.
 *
 * `null` means the expected absence (404): this deployment serves no snapshot, which is not a
 * failure and is not retried. A throw means a feed that should have worked.
 *
 * There is deliberately no `304` branch. The backend does answer `If-None-Match` with 304, but a
 * browser `fetch` never surfaces it: the conditional request is the browser's own, and it resolves
 * the 304 against its HTTP cache and hands JS a 200 with the cached body. `res.status === 304` here
 * would be unreachable code (a non-browser client that sets the header itself owns that branch).
 */
async function fetchSnapshot(): Promise<SnapshotPayload | null> {
  for (let attempt = 0; ; attempt++) {
    let res: Response;
    try {
      res = await fetch(SNAPSHOT_URL, {
        credentials: "include",
        headers: { Accept: "application/json" },
      });
    } catch (err) {
      // Network-level: no status to reason about, so it is transient by definition.
      if (attempt >= ATTEMPTS - 1) {
        throw err;
      }
      await wait(300 * (attempt + 1));
      continue;
    }
    if (res.status === 404) {
      return null;
    }
    if (res.ok) {
      return (await res.json()) as SnapshotPayload;
    }
    if (!transient(res.status) || attempt >= ATTEMPTS - 1) {
      throw new Error(`snapshot HTTP ${res.status} — the feed failed`);
    }
    logger.warn(`[data] snapshot HTTP ${res.status}, retrying`);
    await wait(300 * (attempt + 1));
  }
}

function load(): Promise<Hydrated | null> {
  inflight ??= fetchSnapshot()
    .then(async (payload) => {
      if (!payload) {
        // 404: no snapshot behind this deployment. Demo data under the demo labels, which is what
        // that deployment is — not a degradation to report.
        logger.info("[data] no snapshot served here — demo data");
        return null;
      }
      const started = performance.now();
      const h = hydrate(payload);
      reportNarratives(h);
      logger.info(
        "[data] snapshot loaded:",
        h.skus.length,
        "SKUs,",
        CHANNELS.length,
        "channels,",
        GEO_MARKETS.length,
        "markets,",
        Math.round(performance.now() - started),
        "ms",
      );
      return h;
    })
    .catch((err: unknown) => {
      // G-005: a missing or broken snapshot must not blank the screen — serve the demo field
      // and say, in the badge, that it is demo data.
      logger.warn(
        `[data] snapshot unavailable after ${ATTEMPTS} attempts, falling back to demo data:`,
        err,
      );
      degraded = err instanceof Error ? err.message : String(err);
      // Past the retries above, the fallback is memoised like a success — STICKY for the session,
      // deliberately. The queries are `staleTime: Infinity`, so a retry that succeeded after one
      // method had already served demo SKUs would hydrate the real calibration in place UNDER a
      // cached demo field — demo velocities against a real velMax, a field that describes neither
      // drop. Screens read the tables and the SKUs from the same source or from neither, and
      // `prefetchFieldData` fires two methods at once, so "retry after the fallback but before
      // anything was served" is not a state that exists. Clearing `inflight` here (and rethrowing)
      // would trade a labelled degradation for an unlabelled mixture, or for a blank screen.
      // ponytail: transient failures are retried in `fetchSnapshot`, where it is safe; past that the
      // recovery is a page reload, which the caption says outright. A live retry needs the whole
      // query cache invalidated in the same tick as the hydrate — build that only if a deployment
      // has to recover without a reload.
      return null;
    });
  return inflight;
}

/**
 * Labels for the fallback: the demo ones when there is genuinely no snapshot here, and an explicit
 * failure notice when there should have been one. Same shape either way, so no caller branches.
 */
function fallbackLabels(): DataLabels {
  if (!degraded) {
    return DEMO_LABELS;
  }
  return {
    badge: "Demo data · snapshot unavailable",
    caption: `This deployment serves a real snapshot, but it could not be loaded (${degraded}). Every figure on screen is demo data — reload the page to try again.`,
    notes: [`snapshot feed — not loaded: ${degraded}. The figures below are the demo fixtures.`],
  };
}

/** Provenance labels for the rail / report badges — resolves without blocking on a render. */
export function snapshotLabels(): Promise<DataLabels> {
  return load().then((h) => h?.labels ?? fallbackLabels());
}

export const snapshotDataProvider: DataProvider = {
  getSkus: () => load().then((h) => h?.skus ?? demoDataProvider.getSkus()),
  getSite: () => load().then((h) => h?.payload.site ?? demoDataProvider.getSite()),
  getCapacity: () => load().then((h) => h?.payload.capacity ?? demoDataProvider.getCapacity()),
  getLevers: () => load().then((h) => h?.payload.levers ?? demoDataProvider.getLevers()),
  getLeverFamilies: (leverId: LeverId) =>
    load().then((h): LeverFamily[] | Promise<LeverFamily[]> => {
      if (!h) {
        return demoDataProvider.getLeverFamilies(leverId);
      }
      return leverId === "cap" ? [] : (h.payload.leverFamilies[leverId] ?? []);
    }),
  getReportRows: (reportId: ReportId) =>
    load().then((h) => h?.payload.reports[reportId] ?? demoDataProvider.getReportRows(reportId)),
  askAgent: (question: string, context: AgentContext) =>
    load().then((h) => {
      if (!h) {
        return demoDataProvider.askAgent(question, context);
      }
      return context.surface === "capacity"
        ? capacityAnswer(question, context, h.anchors)
        : fieldAnswer(question, context, h.anchors);
    }),
};
